import { useParams, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import { articleBySlug, relatedArticles } from '../data/articles';

function renderMarkdown(content) {
  const blocks = content.split('\n\n');
  return blocks.map((b, i) => {
    if (b.startsWith('## ')) return <h2 key={i} className="font-display text-2xl font-bold text-[#0A1F44] mt-10 mb-3">{b.slice(3)}</h2>;
    if (b.startsWith('### ')) return <h3 key={i} className="font-display text-xl font-bold text-[#0A1F44] mt-6 mb-2">{b.slice(4)}</h3>;
    if (b.startsWith('- ')) {
      const items = b.split('\n').map((l) => l.replace(/^- /, ''));
      return <ul key={i} className="list-disc list-inside space-y-1 text-[#334155] my-4">{items.map((it, j) => (<li key={j}>{it}</li>))}</ul>;
    }
    if (/^\d+\. /.test(b)) {
      const items = b.split('\n').map((l) => l.replace(/^\d+\. /, ''));
      return <ol key={i} className="list-decimal list-inside space-y-1 text-[#334155] my-4">{items.map((it, j) => (<li key={j}>{it}</li>))}</ol>;
    }
    if (b.startsWith('**') && b.endsWith('**')) return <p key={i} className="font-semibold text-[#0A1F44] my-3">{b.slice(2, -2)}</p>;
    return <p key={i} className="text-[#334155] leading-relaxed my-4">{b}</p>;
  });
}

export default function Article() {
  const { slug } = useParams();
  const a = articleBySlug(slug);
  if (!a) return <Layout><div className="p-16 text-center">Artikkel ikke funnet.</div></Layout>;
  const related = relatedArticles(slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: a.title,
    author: { '@type': 'Organization', name: 'Specialists.no' },
    publisher: { '@type': 'Organization', name: 'Specialists.no' },
    datePublished: a.date,
  };

  return (
    <Layout>
      <Seo
        title={`${a.title} | Specialists.no`}
        description={a.excerpt}
        canonical={`https://specialists.no/artikler/${a.slug}`}
        jsonLd={jsonLd}
      />
      <section className="bg-[#0A1F44] text-white py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-white/50 mb-3">
            <Link to="/" className="hover:text-white">Hjem</Link> / <Link to="/artikler" className="hover:text-white">Artikler</Link> / <span className="text-white">{a.title}</span>
          </nav>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B82F6] mb-4">{a.category} · {a.readingTime} min lesing</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold leading-[1.1]">{a.title}</h1>
        </div>
      </section>

      <article className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg">{renderMarkdown(a.content)}</div>

          <div className="mt-12 bg-white border-2 border-[#3B82F6] rounded-2xl p-7">
            <h3 className="font-display text-xl font-bold text-[#0A1F44] mb-2">Klar til å finne din spesialist?</h3>
            <p className="text-[#64748B] mb-5 text-sm">Kun 4+ stjerner klinikker. Gratis og uforpliktende.</p>
            <Link to="/#lead-form" className="inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white px-6 py-3 rounded-full font-semibold">Send forespørsel <ArrowRight size={16} /></Link>
          </div>

          <div className="mt-8 text-sm text-[#64748B]">Skrevet av <span className="font-semibold text-[#0A1F44]">Specialists.no redaksjonen</span> · {a.date}</div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-[#FAF8F5] py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="font-display text-2xl font-bold text-[#0A1F44] mb-6">Relaterte artikler</h3>
            <div className="grid md:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/artikler/${r.slug}`} className="bg-white rounded-xl p-5 border border-[#0A1F44]/10 hover:-translate-y-0.5 transition">
                  <div className="text-[10px] uppercase tracking-wider text-[#3B82F6] font-semibold mb-2">{r.category}</div>
                  <div className="font-display text-lg font-bold text-[#0A1F44] leading-tight">{r.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
